import { getProductImageURL } from '@noths/polaris-client-ribbons-design-system';
import { selectFavouritesSummary, selectIsFavourite } from '@noths/polaris-client-user-favourites';
import { CurrencyCode } from '@noths/polaris-dev-ts-types';
import { createSelector } from '@reduxjs/toolkit';

import { removeAllHtmltags } from 'src/utils/removeAllHtmlTags';
import { ReduxApplicationState } from './combinedReducer';
import { ProductState } from './product';

export type FavouriteProduct = ProductState & { isFavourited: boolean };

export const selectProduct = (state: ReduxApplicationState) => state.product;

export const selectProductDeliveryByEvent = (state: ReduxApplicationState) =>
  state.product.deliveryByEvent;

export const selectProductDeliveryOptions = (state: ReduxApplicationState) =>
  state.product.deliveryOptions;

export const selectProductPrice = (state: ReduxApplicationState) => state.product.price;

export const selectProductReviews = (state: ReduxApplicationState) => state.reviews;

export const selectProductPartner = (state: ReduxApplicationState) => state.product.partner;

export const selectPersonalisationOptions = createSelector(
  [selectProduct],
  (product) => product.options,
);

export const selectFavouriteProduct = createSelector(
  [selectFavouritesSummary, selectProduct],
  (favouritesSummary, product) => {
    const isFavourited = selectIsFavourite(favouritesSummary, product.code);

    return { ...product, isFavourited };
  },
);

export interface ProductJSONLDSchema {
  '@context': 'https://schema.org';
  '@type': 'Product';
  name: string;
  description: string;
  image: string;
  sku: number;
  brand: {
    '@type': 'Brand';
    name: string;
  };
  offers: {
    '@type': 'Offer';
    priceCurrency: CurrencyCode;
    price: string;
    availability: 'https://schema.org/InStock' | 'https://schema.org/OutOfStock';
    url: string;
  };
  aggregateRating?: {
    '@type': 'AggregateRating';
    ratingValue: number;
    reviewCount: number;
  };
}

export const selectProductJSONLDSchema = createSelector(
  selectProduct,
  (product): ProductJSONLDSchema => ({
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.title,
    description: removeAllHtmltags(
      product.description +
        product.otherInformation +
        product.materials +
        product.variationsAvailable +
        product.dimensions,
    ),
    image: getProductImageURL(product.images?.[0]?.href || '', 'preview'),
    sku: product.code,
    brand: { '@type': 'Brand', name: product.partner.name },
    offers: {
      '@type': 'Offer',
      priceCurrency: product.price.currency,
      price: String(product.price.amount / 100),
      availability:
        product.inventoryStatus === 'in_stock'
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
      url: product.links?.[0]?.href || '',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: product.productRating as number,
      reviewCount: product.numberOfReviews,
    },
  }),
);
